import { createRoot } from 'react-dom/client';
import TagManager from 'react-gtm-module';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
// root app page
import App from 'app';
// providers
import { AuthCompanyGuard } from 'components/auth-company-guard';
import { ProjectProvider } from 'hooks/useProjectDetails';
import { ToastContextProvider } from 'hooks/useToast';
import { ProjectStepperContextProvider } from './hooks/useProjectStepperState';
// store
import { store } from 'redux-store/store';
// styles
import { InAppNotifications } from 'app/pk-in-app';
import { ClientErrorReportingSetup } from 'utils/observability/client-error-reporting-setup';
import 'swiper/css';
import { getEnvironmentConfig } from 'utils/environment';
import './index.css';

declare global {
  interface Window {
    jQuery?: any;
  }
}

// Load jQuery before GTM if not already loaded (required by GTM tags)
const loadJQueryIfNeeded = (): Promise<void> => {
  return new Promise((resolve) => {
    if (window.jQuery) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://code.jquery.com/jquery-3.7.1.min.js';
    script.integrity = 'sha256-/JqT3SQfawRcv/BIHPThkBvs0OEvtFFmqPF/lYI/Cxo=';
    script.crossOrigin = 'anonymous';
    script.onload = () => resolve();
    script.onerror = () => {
      console.warn('Failed to load jQuery, GTM may not work correctly');
      resolve(); // Continue anyway
    };
    document.head.appendChild(script);
  });
};

const { isProduction } = getEnvironmentConfig();

const tagManagerArgs = {
  gtmId: 'GTM-T8FPR29',
};
console.log('App Env: ', process.env.REACT_APP_ENV);
if (isProduction) {
  loadJQueryIfNeeded().then(() => {
    TagManager.initialize(tagManagerArgs);
  });
}

const domNode = document.getElementById('root')!;
const root = createRoot(domNode);

root.render(
  <BrowserRouter>
    <ClientErrorReportingSetup />
    <Provider store={store}>
      <ProjectProvider>
        <ProjectStepperContextProvider>
          <ToastContextProvider>
            <App />
            <AuthCompanyGuard>
              <InAppNotifications />
            </AuthCompanyGuard>
          </ToastContextProvider>
        </ProjectStepperContextProvider>
      </ProjectProvider>
    </Provider>
  </BrowserRouter>
);
